import Decimal from 'decimal.js';

/* ===============================
   ROUNDING CONFIG
   =============================== */

const ROUNDING_MAP = {
    round: Decimal.ROUND_HALF_UP,
    roundDown: Decimal.ROUND_FLOOR,
    truncate: Decimal.ROUND_DOWN,
};

/* ===============================
   OPTIONS
   =============================== */

function normalizeOptions(opts) {
    return {
        precision: opts?.precision ?? 2,
        roundingMode: opts?.roundingMode ?? 'round',
        formatCurrency: opts?.formatCurrency ?? false,
        trailingZeros: opts?.trailingZeros ?? false,
        currency: opts?.currency ?? 'USD',
        locale: opts?.locale ?? 'en-US',
    };
}

function applyPrecision(value, precision, roundingMode) {
    return value.toDecimalPlaces(precision, ROUNDING_MAP[roundingMode]);
}

function formatNumber(value, options) {
    let numStr;

    if (options.trailingZeros) {
        numStr = value.toFixed(options.precision);
    } else {
        numStr = value.toString();
    }

    if (!options.formatCurrency) return numStr;

    return new Intl.NumberFormat(options.locale, {
        style: 'currency',
        currency: options.currency,
        minimumFractionDigits: options.trailingZeros ? options.precision : 0,
        maximumFractionDigits: options.trailingZeros ? options.precision : 20,
    }).format(Number(numStr));
}

/* ===============================
   FINALIZATION (ONLY PLACE WE ROUND)
   =============================== */

function finalize(decimalValue, opts) {
    const o = normalizeOptions(opts);
    const rounded = applyPrecision(decimalValue, o.precision, o.roundingMode);
    return formatNumber(rounded, o);
}

/* ===============================
   RAW DECIMAL MATH (NO ROUNDING)
   =============================== */

export const Raw = {
    add: (a, b) => new Decimal(a).add(b),
    sub: (a, b) => new Decimal(a).sub(b),
    mul: (a, b) => new Decimal(a).mul(b),
    div: (a, b) => new Decimal(a).div(b),
};

/* ===============================
   NUMERIC ENGINE
   =============================== */

export const NumericEngine = {
    /** FORMAT ONLY */
    format(value, opts) {
        const o = normalizeOptions(opts);
        return finalize(new Decimal(value), o);
    },

    add(a, b, opts) {
        const o = normalizeOptions(opts);
        return finalize(new Decimal(a).add(b), o);
    },

    subtract(a, b, opts) {
        const o = normalizeOptions(opts);
        return finalize(new Decimal(a).sub(b), o);
    },

    multiply(a, b, opts) {
        const o = normalizeOptions(opts);
        return finalize(new Decimal(a).mul(b), o);
    },

    divide(a, b, opts) {
        const o = normalizeOptions(opts);
        return finalize(new Decimal(a).div(b), o);
    },

    /* ===============================
       PRICING & MARGIN UTILITIES
       =============================== */

    /**
     * retail = cost * (1 + markup% / 100)
     */
    calculateRetailFromMarkup(cost, markupPercentage, opts) {
        if (cost == null || markupPercentage == null) return 0;

        const markupRatio = Raw.div(markupPercentage, 100);
        const multiplier = Raw.add(1, markupRatio);
        const retail = Raw.mul(cost, multiplier);

        return finalize(retail, opts);
    },

    /**
     * retail = cost / (1 - margin% / 100)
     */
    calculateRetailFromMargin(cost, marginPercentage, opts) {
        if (cost == null || marginPercentage == null) return 0;

        const marginRatio = Raw.div(marginPercentage, 100);
        const denominator = Raw.sub(1, marginRatio);

        if (denominator.lte(0)) return 0;

        const retail = Raw.div(cost, denominator);
        return finalize(retail, opts);
    },

    /**
     * markup% = ((retail - cost) / cost) * 100
     */
    calculateMarkupPercentageFromRetail(cost, retailPrice, opts) {
        if (cost == null || cost <= 0 || retailPrice == null) return 0;

        const profit = Raw.sub(retailPrice, cost);
        const ratio = Raw.div(profit, cost);
        const markup = Raw.mul(ratio, 100);

        return finalize(markup, opts);
    },

    /**
     * margin% = ((retail - cost) / retail) * 100
     */
    calculateMarginPercentageFromRetail(cost, retailPrice, opts) {
        if (retailPrice == null || retailPrice <= 0 || cost == null) return 0;

        const profit = Raw.sub(retailPrice, cost);
        const ratio = Raw.div(profit, retailPrice);
        const margin = Raw.mul(ratio, 100);

        return finalize(margin, opts);
    },
};
